import {
  Box,
  Button,
  Center,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputLeftAddon,
  Radio,
  RadioGroup,
  Show,
  Spacer,
  Stack,
  Text,
  useBreakpointValue,
  VStack,
} from "@chakra-ui/react";
import { ChangeEvent, useEffect, useState } from "react";
import { SignupUnit, VACANT } from "./SignupFlow";
import { Discipline_GET } from "../../../Api/dto/Discipline_GET";
import { Competition_GET } from "../../../Api/dto/Competition_GET";
import { SignupRequest } from "../../../Api/dto/SignupRequest_POST";
import { Gender } from "../../../Api/dto/Discipline_POST_PUT";
import BirthDatePicker from "../../DatePicker/DatePicker";
import { is } from "immutable";
import InfoPopover from "../../Helpers/InfoPopover";

const maxLimitBirthYear = 2496;
const minLimitBirthYear = 1900;

type TeamType = "Östhammar Friidrott" | "Annan förening" | "Ingen förening";
const teamTypeOfi: TeamType = "Östhammar Friidrott";
const teamOther: TeamType = "Annan förening";
const teamNone: TeamType = "Ingen förening";

export function SelectParticipants(props1: {
  startState: SignupUnit;
  onNext: (updatedUnit: SignupUnit) => void;
  onPrev: () => void;
  occupiedTeamNames: Set<string>;
  discipline: Discipline_GET | undefined;
  competition: Competition_GET | null;
}) {
  const [changedSignupUnit, setChangedSignupUnit] = useState<SignupUnit>(
    props1.startState.map((s) => ({
      ...s,
      gender:
        s.gender ??
        (props1.discipline?.genderRequirements.length === 1
          ? props1.discipline?.genderRequirements[0]
          : undefined),
    }))
  );

  const [hasErrors, setHasErrors] = useState(false);

  const [groupNameError, setGroupNameError] = useState<string | undefined>(
    undefined
  );

  const [selectedGroupName, setSelectedGroupName] = useState<
    undefined | string
  >(props1.startState[0].groupName);

  useEffect(() => {
    const groupNameAlreadyOccupied = props1.occupiedTeamNames.has(
      selectedGroupName ?? "x"
    );

    setGroupNameError(
      changedSignupUnit.length < 2
        ? undefined
        : selectedGroupName == null
        ? "Ange ett lagnamn"
        : selectedGroupName.length < 3
        ? "Lagnamnet är för kort"
        : selectedGroupName.length > 30
        ? "Lagnamnet är för långt"
        : groupNameAlreadyOccupied
        ? "Lagnamnet är redan upptaget, välj ett nytt"
        : undefined
    );
  }, [selectedGroupName, props1.occupiedTeamNames]);

  return (
    <VStack spacing={5} background={"white"} paddingTop={"80px"}>
      {/*<Heading size="xl" marginBottom={"22px"}>
        {changedSignupUnit.length < 2
          ? `Anmälan till ${props1.discipline?.name}`
          : `Laganmälan till ${props1.discipline?.name}`}
      </Heading>
        */}
      {changedSignupUnit.length > 1 && (
        <InputGroup width={"80%"} marginBottom={"50px"}>
          <InputLeftAddon>Lagnamn</InputLeftAddon>
          <Input
            borderColor={"gray.500"}
            value={selectedGroupName ?? ""}
            onChange={(e) => {
              setSelectedGroupName(e.target.value);
            }}
          />
          <FormErrorMessage>{groupNameError}</FormErrorMessage>
        </InputGroup>
      )}

      {groupNameError ||
        changedSignupUnit.map((signup, i) => (
          <PersonInputfields
            key={`${i}_pinput`}
            index={i}
            signup={signup}
            onChange={(newPerson, errorMessages) => {
              setHasErrors(errorMessages.filter((e) => e != null).length > 0);
              setChangedSignupUnit((old) => {
                const updatesSignups = [...changedSignupUnit];
                updatesSignups[i] = newPerson;
                return updatesSignups;
              });
            }}
            discipline={props1.discipline}
            competition={props1.competition}
          />
        ))}
      <HStack margin={"80px"} marginTop={"30px"}>
        <Spacer />
        <Button
          width={""}
          fontSize={"20px"}
          variant="ghost"
          mr={3}
          type="button"
          onClick={(e) => {
            e.preventDefault();
            props1.onPrev();
          }}
        >
          Tillbaka
        </Button>
        <Button
          colorScheme="pink"
          fontSize={"20px"}
          type="submit"
          isDisabled={hasErrors || groupNameError != null}
          onClick={(e) => {
            e.preventDefault();
            props1.onNext(
              changedSignupUnit.map((s) => {
                return { ...s, groupName: selectedGroupName };
              })
            );
          }}
        >
          Nästa
        </Button>
        <Spacer />
        <Spacer />
      </HStack>
    </VStack>
  );
}

function PersonInputfields(props: {
  index: number;
  signup: SignupRequest;
  onChange: (
    person: SignupRequest,
    errorMessages: Array<string | undefined>
  ) => void;
  discipline: Discipline_GET | undefined;
  competition: Competition_GET | null;
}) {
  const [person, setPerson] = useState<SignupRequest>({ ...props.signup });

  const [firstNameError, setFirstNameError] = useState<string | undefined>(
    undefined
  );
  const [lastNameError, setLastNameError] = useState<string | undefined>(
    undefined
  );
  const [fullBirthDateError, setFullBirthDateError] = useState<
    string | undefined
  >(undefined);

  const [genderError, setGenderError] = useState<string | undefined>(undefined);

  const [clubError, setClubError] = useState<string | undefined>(undefined);

  const [isVacant, setIsVacant] = useState(
    person.firstname === VACANT && person.lastname === VACANT
  );

  const isBelowSm = useBreakpointValue({ base: true, sm: false, });

  const isDM = props.discipline?.name.match(/^V?DM.*/) ? true : false;

  useEffect(() => {
    if (props.discipline?.genderRequirements.length == 1) {
      setPerson((old) => ({
        ...old,
        gender: props.discipline?.genderRequirements[0],
      }));
    }
  }, []);

  const [selectedSportsTeamType, setSelectedSportsTeamType] = useState<string>(
    isDM ? teamOther : teamNone
  );

  const handleChangeOfSocialSecurityNr = (e: ChangeEvent<HTMLInputElement>) => {
    const formattedValue = e.target.value.replace(/\D/g, "").slice(0, 4);
    setPerson((old) => {
      return { ...old, socialSecurityNumber: formattedValue};
    });
  };

  useEffect(() => {
    setIsVacant(person.firstname === VACANT && person.lastname === VACANT);

    setFirstNameError(
      person.firstname == null || person.firstname.trim().length == 0
        ? "Ange förnamn"
        : person.firstname.length > 50
        ? "Förnamn får max vara 50 bokstäver långt"
        : undefined
    );
    setLastNameError(
      person.lastname == null || person.lastname.trim().length == 0
        ? "Ange efternamn"
        : person.lastname.length > 50
        ? "Efternamn får max vara 50 bokstäver långt"
        : undefined
    );

    const highestBirthYearValue =
      ageToBirthYear(props.discipline?.minAge, props.competition?.startTime) ??
      (props.competition?.startTime
        ? new Date(props.competition!.startTime).getFullYear()
        : maxLimitBirthYear);
    const lowestBirthYearValue =
      ageToBirthYear(props.discipline?.maxAge, props.competition?.startTime) ??
      minLimitBirthYear;

    setFullBirthDateError(
      isVacant && person.fullBirthDate == null
        ? undefined
        : person.fullBirthDate == null
        ? "Ange födelsedatum"
        : person.fullBirthDate.getFullYear() > highestBirthYearValue
        ? `Min ålder: födda ${Math.min(
            highestBirthYearValue,
            props.competition?.startTime
              ? new Date(props.competition?.startTime).getFullYear()
              : new Date().getFullYear()
          )}`
        : person.fullBirthDate.getFullYear() < lowestBirthYearValue
        ? `Max ålder: födda ${lowestBirthYearValue}`
        : undefined
    );

    setGenderError(
      !isVacant &&
        (props.discipline?.genderRequirements?.length ?? 0) > 0 &&
        person.gender == null
        ? "Måste göra ett val"
        : undefined
    );

    setClubError(
      selectedSportsTeamType != teamNone && !person.sportsTeam
        ? "Måste ange en klubb"
        : undefined
    );
  }, [person, isVacant, selectedSportsTeamType]);

  useEffect(() => {
    props.onChange(person, [
      firstNameError,
      lastNameError,
      fullBirthDateError,
      genderError,
      clubError,
    ]);
  }, [
    person,
    firstNameError,
    lastNameError,
    fullBirthDateError,
    genderError,
    clubError,
  ]);

  return (
    <VStack shadow={"2xl"} border={"1px"} width={"96%"}>
      <Box bg="lightblue" p={2} width={"100%"}>
        <Center>
          <Heading as="h4" size={"lg"}>
            Ny deltagare{" "}
            {(props.discipline?.groupSize ?? 1) > 1 &&
              ` (${props.index + 1}/${props.discipline?.groupSize ?? 1})`}
          </Heading>
        </Center>
      </Box>
      <Box padding={"5px"} paddingTop={"30px"} paddingBottom={"30px"}>
        <FormControl isRequired isInvalid={!!firstNameError} marginTop={"20px"}>
          <FormLabel>Förnamn</FormLabel>
          <Input
            borderColor={"gray.500"}
            value={person.firstname || ""}
            onChange={(e) => {
              setPerson((old) => {
                return { ...old, firstname: e.target.value };
              });
            }}
          />
          <FormErrorMessage>{firstNameError}</FormErrorMessage>
        </FormControl>
        <FormControl isRequired isInvalid={!!lastNameError} marginTop={"20px"}>
          <FormLabel>Efternamn</FormLabel>
          <Input
            value={person.lastname}
            borderColor={"gray.500"}
            onChange={(e) => {
              setPerson((old) => {
                return { ...old, lastname: e.target.value };
              });
            }}
          />
          <FormErrorMessage>{lastNameError}</FormErrorMessage>
        </FormControl>

        <FormControl
          isRequired={person.firstname != VACANT}
          marginTop={"20px"}
          isInvalid={
            !isVacant &&
            person.fullBirthDate == null
          }
        >
          <HStack align="center"> 
            <FormLabel m={0}>Födelsedatum</FormLabel>
            <InfoPopover content=
            "Endast födelseåret visas i våra listor. Fullständigt datum krävs sedan 2026 för korrekt resultatrapportering till Svenska Friidrottsförbundet."
            />
          </HStack>
          <BirthDatePicker
            direction={isBelowSm ? "column" : "row"}
            maxYear={
              ageToBirthYear(
                props.discipline?.minAge,
                props.competition?.startTime
              ) ??
              (props.competition?.startTime
                ? new Date(props.competition.startTime).getFullYear()
                : maxLimitBirthYear)
            }
            minYear={
              ageToBirthYear(
                props.discipline?.maxAge,
                props.competition?.startTime
              ) ?? minLimitBirthYear
            }
            onChange={(date) => {
              setPerson((old) => {
                return { ...old, fullBirthDate: date || undefined, fullBirthYear: date?.getFullYear() || undefined};
              });
            }}
            value={person.fullBirthDate}
          />
          <FormErrorMessage>{fullBirthDateError}</FormErrorMessage>
        </FormControl>
        <FormControl
          isRequired={
            isVacant && (props.discipline?.genderRequirements?.length || 0) > 1
          }
          isInvalid={
            !isVacant &&
            (props.discipline?.genderRequirements?.length || 0) > 1 &&
            person.gender == null
          }
          marginTop={"20px"}
          hidden={
            isVacant || (props.discipline?.genderRequirements?.length || 0) < 2
          }
        >
          <FormLabel>Kön </FormLabel>
          <RadioGroup
            marginLeft={"10px"}
            onChange={(e) => {
              setPerson((old) => {
                return {
                  ...old,
                  gender: e === "MAN" ? Gender.MAN : Gender.WOMAN,
                };
              });
            }}
            value={
              person.gender == Gender.MAN
                ? "MAN"
                : person.gender == Gender.WOMAN
                ? "WOMAN"
                : ""
            }
          >
            <Stack direction="column">
              <Radio borderColor={"gray.500"} value={"MAN"}>
                Man/Pojke
              </Radio>
              <Radio borderColor={"gray.500"} value={"WOMAN"}>
                Kvinna/Flicka
              </Radio>
            </Stack>
          </RadioGroup>
          <FormErrorMessage>{genderError}</FormErrorMessage>
        </FormControl>
        <FormControl marginTop={"20px"}>
          <FormLabel>Förening</FormLabel>
          <RadioGroup
            marginLeft={"10px"}
            onChange={(e) => {
              setSelectedSportsTeamType(e);
            }}
            value={selectedSportsTeamType}
          >
            <Stack direction="column">
              <Radio
                isDisabled={
                  props.discipline?.name.match(/^V?DM.*/) ? true : false
                }
                borderColor={"gray.500"}
                value={teamNone}
                onChange={(e) => {
                  setPerson((old) => {
                    return {
                      ...old,
                      socialSecurityNumber: undefined,
                      sportsTeam: undefined,
                    };
                  });
                }}
              >
                {teamNone}
              </Radio>
              <Radio
                colorScheme="green"
                borderColor={"gray.500"}
                value={teamTypeOfi}
                onChange={(e) => {
                  setPerson((old) => {
                    return {
                      ...old,
                      sportsTeam: teamTypeOfi,
                    };
                  });
                }}
              >
                {teamTypeOfi}
              </Radio>
              {selectedSportsTeamType == teamTypeOfi && (
                <>
                  <Show above="sm">
                    <HStack marginLeft={"10px"}>
                      <FormLabel>Personnummer: </FormLabel>
                      <InputGroup>
                      <InputLeftAddon px={4}>
                        <Text
                        letterSpacing="1px" fontFamily="mono"
                        >
                          {person.fullBirthDate == null ? "YYYYMMDD - " : `${birthDateToString(person.fullBirthDate)} - `}
                        </Text>
                      </InputLeftAddon>
                      <Input
                        onChange={handleChangeOfSocialSecurityNr}
                        value={person.socialSecurityNumber}
                        borderColor={"gray.500"}
                        placeholder="XXXX"
                        width={"100px"}
                      />
                      </InputGroup>
                    </HStack>
                  </Show>
                  <Show below="sm">
                    <VStack marginLeft={"20px"} align={"left"}>
                      <FormLabel>Personnummer: </FormLabel>

                      <InputGroup>
                      <InputLeftAddon px={4}>
                        <Text
                        letterSpacing="1px" fontFamily="mono"
                        >
                          {person.fullBirthDate == null ? "YYYYMMDD - " : `${birthDateToString(person.fullBirthDate)} - `}
                        </Text>
                      </InputLeftAddon>
                      <Input
                        onChange={handleChangeOfSocialSecurityNr}
                        value={person.socialSecurityNumber}
                        borderColor={"gray.500"}
                        placeholder="XXXX"
                        width={"100px"}
                      />
                      </InputGroup>
                    </VStack>
                  </Show>
                </>
              )}
              <Radio
                onChange={(e) => {
                  setPerson((old) => {
                    return {
                      ...old,
                      socialSecurityNumber: undefined,
                      sportsTeam: undefined,
                    };
                  });
                }}
                borderColor={"gray.500"}
                value={teamOther}
              >
                {teamOther}
              </Radio>
              {selectedSportsTeamType == teamOther && (
                <>
                  <Show above="sm">
                    <FormControl
                      isRequired={selectedSportsTeamType == teamOther}
                      isInvalid={!!clubError}
                      marginTop={"20px"}
                    >
                      <FormLabel>Föreningsnamn: </FormLabel>
                      <Input
                        onChange={(e) => {
                          setPerson((old) => {
                            return { ...old, sportsTeam: e.target.value };
                          });
                        }}
                        value={person.sportsTeam}
                        borderColor={"gray.500"}
                        placeholder="Föreningsnamn"
                      />
                    </FormControl>
                  </Show>
                  <Show below="sm">
                    <FormControl
                      isRequired={selectedSportsTeamType == teamOther}
                      isInvalid={!!clubError}
                      marginTop={"20px"}
                    >
                      <FormLabel>Föreningsnamn: </FormLabel>
                      <Input
                        onChange={(e) => {
                          setPerson((old) => {
                            return {
                              ...old,
                              sportsTeam: e.target.value,
                            };
                          });
                        }}
                        value={person.sportsTeam}
                        borderColor={"gray.500"}
                        placeholder="Föreningsnamn"
                      />
                    </FormControl>
                  </Show>
                </>
              )}
            </Stack>
          </RadioGroup>
        </FormControl>
      </Box>
    </VStack>
  );
}

function ageToBirthYear(
  ageInYears: number | undefined | null,
  eventStartDateInMs: number | undefined | null
) {
  if (ageInYears == null) {
    return undefined;
  }
  const date =
    eventStartDateInMs == null ? new Date() : new Date(eventStartDateInMs);
  return date.getFullYear() - ageInYears;
}

/// Returns date informat "yyyyMMDD"
export function birthDateToString(birthDate: Date | undefined | null): string {
  if (!birthDate) return "";
  const year = birthDate.getFullYear().toString().padStart(4, "0");
  const month = (birthDate.getMonth() + 1).toString().padStart(2, "0");
  const day = birthDate.getDate().toString().padStart(2, "0");
  return `${year}${month}${day}`;
}